import { ExternalLink, Navigation, Star, Truck, Car } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const Pill = ({ children, tone = "muted", testId }) => (
  <span data-testid={testId} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${tone === "ok" ? "bg-chenia-sage/30 text-foreground" : tone === "warn" ? "bg-chenia-rosesoft text-chenia-terracottadark" : "bg-secondary text-muted-foreground"}`}>{children}</span>
);

const Ext = ({ href, children, testId }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId}
    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-chenia-terracottadark underline-offset-2 hover:underline">
    {children}<ExternalLink className="h-3 w-3" />
  </a>
);

// Magasin proche (mode drive) — « confirmé » uniquement si drive_status === confirmed
export function StoreCard({ store, preferred, onPrefer }) {
  const { t, tv } = useLang();
  const confirmed = store.drive_status === "confirmed";
  return (
    <li className="rounded-2xl border border-border bg-background/60 p-4" data-testid={`store-card-${store.place_id}`}>
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-9 w-9 rounded-xl bg-secondary text-primary shrink-0"><Car className="h-4 w-4" /></span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium truncate" data-testid="store-card-name">{store.name}</p>
            <Pill testId="store-card-distance">{t("st_away", { km: store.distance_km })}</Pill>
            {preferred && <Pill tone="ok" testId="store-card-preferred">{t("st_preferred")}</Pill>}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="store-card-address">{store.address}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {confirmed ? <Pill tone="ok" testId="store-card-drive-confirmed">{t("st_mode_drive")} · {t("st_services_confirmed")}</Pill>
              : <Pill tone="warn" testId="store-card-drive-unverified">{t("st_to_verify")}</Pill>}
            {!confirmed && store.drive_hint && <Pill testId="store-card-drive-hint">{t("st_drive_hint")}</Pill>}
            {store.online_only && <Pill>{t("st_online_only")}</Pill>}
          </div>
          {store.note && <p className="text-[11px] text-muted-foreground mt-1">{tv(store.note)}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
            <Ext href={store.official_url} testId="store-card-site">{t("st_open_site")}</Ext>
            {store.maps_url && <Ext href={store.maps_url} testId="store-card-maps"><Navigation className="h-3 w-3" />{t("st_open_maps")}</Ext>}
            <button type="button" onClick={onPrefer} data-testid="store-card-prefer"
              className={`inline-flex items-center gap-1 text-xs ${preferred ? "text-chenia-terracottadark" : "text-muted-foreground hover:text-foreground"}`}>
              <Star className={`h-3.5 w-3.5 ${preferred ? "fill-current" : ""}`} />{preferred ? t("st_remove_preferred") : t("st_set_preferred")}
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}

// Service de livraison d'une enseigne (mode livraison) — jamais déduit de la proximité d'un magasin
export function DeliveryCard({ service, preferred, onPrefer }) {
  const { t, tv } = useLang();
  const confirmed = service.delivery_status === "confirmed";
  return (
    <li className="rounded-2xl border border-border bg-background/60 p-4" data-testid={`delivery-card-${service.retailer_id}`}>
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-9 w-9 rounded-xl bg-secondary text-primary shrink-0"><Truck className="h-4 w-4" /></span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium" data-testid="delivery-card-name">{service.retailer_name}</p>
            {preferred && <Pill tone="ok" testId="delivery-card-preferred">{t("st_preferred")}</Pill>}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {confirmed ? <Pill tone="ok" testId="delivery-card-confirmed">{t("st_mode_delivery")} · {t("st_services_confirmed")}</Pill>
              : <Pill tone="warn" testId="delivery-card-unverified">{t("st_to_verify")}</Pill>}
            {service.online_only && <Pill>{t("st_online_only")}</Pill>}
          </div>
          {service.note && <p className="text-[11px] text-muted-foreground mt-1">{tv(service.note)}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
            <Ext href={service.official_url} testId="delivery-card-site">{t("st_open_site")}</Ext>
            <button type="button" onClick={onPrefer} data-testid="delivery-card-prefer"
              className={`inline-flex items-center gap-1 text-xs ${preferred ? "text-chenia-terracottadark" : "text-muted-foreground hover:text-foreground"}`}>
              <Star className={`h-3.5 w-3.5 ${preferred ? "fill-current" : ""}`} />{preferred ? t("st_remove_preferred") : t("st_set_preferred")}
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}
